import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactDOMServer from "react-dom/server";

import { useRpmPerState } from "@/components/home/useRpmPerState";
import { useMapboxKey } from "@/components/maps/useMapboxKey";
import { ErrorComponent } from "@/components/common/errors";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { API } from "@/services/API";
import { addPin, removePins, fitBounds } from "@/utils/map";
import { MarketPin, PickupPin } from "@/components/common/svgs";
import { useQuery } from "@/utils/query";
import { clearLocations, getPickupLocation } from "@/state/searchSlice";
import { getUsaStates, getMarkets, getMarketCenters } from "@/state/mapDataSlice";
import { HoverPopup } from "@/components/dataPages";

export const PredictionMarketsMap = ({ hasError = false, setHasError = () => {}, outbounds, optimizePer, filterOutbounds }) => {
    const { markets, marketCenters, states } = useSelector((state) => state.mapData);
    const [nationalAverageRPM, setNationalAverageRPM] = useState(null);
    const [shouldSetPins, setShouldSetPins] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(true);
    const { pickupZip } = useQuery();
    const dispatch = useDispatch();
    const { pickupLocation } = useSelector((state) => state.search);

    const mapRef = useRef(null);
    const mapContainer = useRef(null);
    const [mapIsLoaded, setMapIsLoaded] = useState(false);
    const [mapInitialised, setMapInitialised] = useState(false);

    const mapboxAccessKey = useMapboxKey(true);
    const { getColor, getIndexColor } = useRpmPerState();
    const isDarkTheme = useIsDarkTheme();
    const initialBounds = [
        [-126, 50.9],
        [-67.93, 23.3],
    ];
    const padding = { top: 2, bottom: 2, left: 2, right: 2 };

    const handleColorChange = () => {
        const outboundsLength = outbounds ? Object.keys(outbounds).length : 0;

        // If no outbounds, show skeleton
        if (outboundsLength === 0 && mapRef.current && mapInitialised) {
            const map = mapRef.current;
            if (map.getLayer("skeleton-markets-layer")) {
                map.setLayoutProperty("skeleton-markets-layer", "visibility", "visible");
            }
            if (map.getLayer("skeleton-states-layer")) {
                map.setLayoutProperty("skeleton-states-layer", "visibility", "visible");
            }
            if (map.getLayer("markets-layer")) {
                map.setLayoutProperty("markets-layer", "visibility", "none");
            }
            if (map.getLayer("states-border-layer")) {
                map.setLayoutProperty("states-border-layer", "visibility", "none");
            }
            if (map.getLayer("selected-market-layer")) {
                map.removeLayer("selected-market-layer");
            }
            setShowSkeleton(true);
            removePins();
            return;
        }

        if (mapRef.current && mapInitialised && outboundsLength && markets.data && nationalAverageRPM) {
            const map = mapRef.current;

            removePins();
            const newMarketsData = JSON.parse(JSON.stringify(markets.data));
            newMarketsData.features.forEach((feature) => {
                const tempOut = outbounds[feature.properties.marketName];
                const outboundData = tempOut?.filtered || tempOut?.unfiltered;
                const rpm = outboundData?.rpm;
                const rate = outboundData?.rate;
                const confidence = outboundData?.confidence;
                const distance = outboundData?.distance;

                if (optimizePer.id === "rpm") {
                    if (!rpm) {
                        console.log("No outbound value", feature.properties.marketName);
                    }
                    feature.properties.color = getColor(rpm || 0, nationalAverageRPM);
                } else {
                    const sortedIndex = outboundData?.sortIndex;
                    feature.properties.color = getIndexColor(sortedIndex, outboundsLength);
                }
                feature.properties.opacity = outboundData?.filtered ? 1 : 0.1;
                feature.properties.rpm = rpm || 0;
                feature.properties.rate = rate || 0;
                feature.properties.confidence = confidence || 0;
                feature.properties.distance = distance || 0;
            });
            const marketsSource = mapRef.current?.getSource("markets");
            if (!marketsSource) {
                return;
            }
            marketsSource.setData(newMarketsData);

            // Hide skeleton layers and show actual map layers when outbounds data is available
            if (map.getLayer("skeleton-markets-layer")) {
                map.setLayoutProperty("skeleton-markets-layer", "visibility", "none");
            }
            if (map.getLayer("skeleton-states-layer")) {
                map.setLayoutProperty("skeleton-states-layer", "visibility", "none");
            }
            if (map.getLayer("markets-layer")) {
                map.setLayoutProperty("markets-layer", "visibility", "visible");
            }
            if (map.getLayer("states-border-layer")) {
                map.setLayoutProperty("states-border-layer", "visibility", "visible");
            }

            setShowSkeleton(false);
            setShouldSetPins(true);
        }
    };

    useEffect(() => {
        handleColorChange();
    }, [outbounds, optimizePer, nationalAverageRPM, markets.data, mapInitialised]);

    useEffect(() => {
        if (
            !mapInitialised ||
            marketCenters.loading ||
            markets.loading ||
            states.loading ||
            !marketCenters.data ||
            !markets.data ||
            !states.data
        ) {
            return;
        }
        if (shouldSetPins && mapRef.current && filterOutbounds && pickupZip && pickupLocation.data) {
            const pickupPoint = [pickupLocation.data.longitude, pickupLocation.data.latitude];
            const map = mapRef.current;
            if (pickupPoint) {
                addPin(
                    map,
                    () => {
                        return <PickupPin isDark={isDarkTheme} />;
                    },
                    pickupPoint,
                    -17,
                );
                if (map.getLayer("selected-market-layer")) {
                    map.removeLayer("selected-market-layer");
                }
                map.addLayer({
                    id: "selected-market-layer",
                    type: "line",
                    source: "markets",
                    paint: {
                        "line-color": isDarkTheme ? "#8EE4CA" : "#2E5D4B",
                        "line-width": 2,
                        "line-opacity": 1,
                    },
                    filter: ["==", "marketName", pickupLocation.data.market.city],
                });
            }
            filterOutbounds.forEach((market, index) => {
                const marketCenter = marketCenters.data[market.zip];
                if (marketCenter) {
                    const point = [marketCenter.longitude, marketCenter.latitude];
                    addPin(
                        map,
                        () => {
                            return <MarketPin number={index + 1} isDarkTheme={isDarkTheme} />;
                        },
                        point,
                        -13,
                    );
                }
            });
            setShouldSetPins(false);
        }
    }, [shouldSetPins, pickupLocation.data, marketCenters.data]);

    useEffect(() => {
        if (!pickupZip) return;
        dispatch(getPickupLocation({ zip: pickupZip }));
    }, [pickupZip]);

    useEffect(() => {
        if (!states.data) {
            dispatch(getUsaStates());
        }
        if (!markets.data) {
            dispatch(getMarkets());
        }
        if (!marketCenters.data) {
            dispatch(getMarketCenters());
        }

        return () => {
            dispatch(clearLocations());
            removePins();
        };
    }, []);

    useEffect(() => {
        if (mapRef.current && markets.data && states.data && mapIsLoaded && !mapInitialised) {
            const map = mapRef.current;

            map.addSource("markets", {
                type: "geojson",
                data: markets.data,
            });

            map.addLayer({
                id: "markets-layer",
                type: "fill",
                source: "markets",
                layout: {
                    visibility: "none", // Initially hidden until outbounds data loads
                },
                paint: {
                    "fill-color": ["case", ["!=", ["get", "color"], null], ["get", "color"], isDarkTheme ? "#1E382E" : "#D9EDE4"],
                    "fill-opacity": ["case", ["!=", ["get", "opacity"], null], ["get", "opacity"], 1],
                },
            });

            map.addLayer({
                id: "hover-layer",
                type: "fill",
                source: "markets",
                paint: {
                    "fill-color": isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                },
                filter: ["==", "marketName", ""],
            });

            map.addSource("states", {
                type: "geojson",
                data: states.data,
            });

            map.addLayer({
                id: "states-border-layer",
                type: "line",
                source: "states",
                layout: {
                    visibility: "none", // Initially hidden until outbounds data loads
                },
                paint: {
                    "line-color": isDarkTheme ? "#131414" : "#66857B",
                    "line-width": 0.5,
                    "line-opacity": 0.8,
                },
            });

            fitBounds(map, padding, 400);

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: "custom-popup",
                maxWidth: "auto",
            });

            const clearHover = () => {
                map.setFilter("hover-layer", ["==", "marketName", ""]);
                popup.remove();
                map.getCanvas().style.cursor = "";
            };

            // Add mouseenter event to highlight state and show popup
            map.on("mousemove", "markets-layer", (event) => {
                const features = map.queryRenderedFeatures(event.point, {
                    layers: ["markets-layer"],
                });

                if (features.length > 0) {
                    const feature = features[0];
                    const { marketName } = feature.properties;
                    const popupContent = ReactDOMServer.renderToString(
                        <HoverPopup outbound={feature.properties} isDarkTheme={isDarkTheme} />,
                    );
                    map.setFilter("hover-layer", ["==", "marketName", marketName]);
                    popup.setLngLat(event.lngLat).setHTML(popupContent).addTo(map);
                } else {
                    clearHover();
                }
            });

            map.on("mouseleave", "markets-layer", clearHover);
            map.on("mouseenter", "markets-layer", () => {
                map.getCanvas().style.cursor = "pointer";
            });

            map.on("error", async () => {
                setHasError(true);
            });

            setMapInitialised(true);
        }
    }, [markets.data, states.data, mapIsLoaded]);

    useEffect(() => {
        if (mapContainer.current && mapboxAccessKey) {
            mapboxgl.accessToken = mapboxAccessKey;
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/lioneightdev/cm4zv8t6b00cj01sp11qp616w",
                center: [-98.5795, 37.8283],
                dragRotate: false,
                touchZoomRotate: false,
                pitchWithRotate: false,
                dragPan: false,
                bounds: initialBounds,
                fitBoundsOptions: { padding, duration: 400 },
                logoPosition: "bottom-right",
            });
            mapRef.current = map;

            map.on("load", async () => {
                // Add skeleton layers for loading state
                try {
                    const response = await fetch("/usa_continental.json");
                    const usaData = await response.json();

                    map.addSource("skeleton-usa", {
                        type: "geojson",
                        data: usaData,
                    });

                    // Add skeleton fill layer
                    map.addLayer({
                        id: "skeleton-markets-layer",
                        type: "fill",
                        source: "skeleton-usa",
                        paint: {
                            "fill-color": isDarkTheme ? "#1F2321" : "#F1F4F3",
                            "fill-opacity": 1,
                        },
                    });

                    // Add skeleton border layer (no visible borders initially)
                    map.addLayer({
                        id: "skeleton-states-layer",
                        type: "line",
                        source: "skeleton-usa",
                        paint: {
                            "line-color": isDarkTheme ? "#1F2321" : "#F1F4F3",
                            "line-width": 0.5,
                            "line-opacity": 0.3,
                        },
                    });
                } catch (error) {
                    console.error("Failed to load skeleton map:", error);
                }

                const nationalAverageRPM = await API.get("/analytics/national-average-rpm", {
                    params: {
                        trailer: "dry_van",
                        "interval-in-days": "30",
                    },
                })
                    .then((response) => {
                        return response.average;
                    })
                    .catch(() => {
                        setHasError(true);
                    });
                setNationalAverageRPM(nationalAverageRPM);
                setMapIsLoaded(true);
            });

            map.scrollZoom.disable();
            map.doubleClickZoom.disable();

            const handleFitBounds = () => {
                fitBounds(map, padding, 100);
            };

            window.addEventListener("resize", handleFitBounds);

            return () => {
                map.remove();
                window.removeEventListener("resize", handleFitBounds);
            };
        }
    }, [mapboxAccessKey]);

    return hasError ? (
        <ErrorComponent className={"h-full lg:min-h-[367px]"} />
    ) : (
        <div className="relative w-full h-full">
            <div
                ref={mapContainer}
                className="top-[-40px] sm:top-0 left-0 bottom-0 w-full h-full absolute"
                style={{
                    transition: "opacity 0.4s ease-out",
                }}
            />
            {showSkeleton && (
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className={`absolute inset-0 animate-shimmer bg-gradient-to-r ${
                            isDarkTheme
                                ? "from-transparent via-[#131414]/30 to-transparent"
                                : "from-transparent via-[#FFFFFF]/30 to-transparent"
                        }`}
                        style={{
                            animation: "shimmer 2s infinite",
                        }}
                    />
                </div>
            )}
        </div>
    );
};
